import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, tokenStore, depotStore, ownerReturn } from "@/lib/api";

const AuthContext = createContext(null);
const RENEW_EVERY_MS = 12 * 3600 * 1000;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const renewedAt = useRef(0);

  // Keep a remembered login alive: each use pushes the expiry out another 90 days.
  const renew = useCallback(async () => {
    const old = tokenStore.get();
    if (!old) return;
    renewedAt.current = Date.now();
    try {
      const { token } = await api.renew();
      if (token && tokenStore.get() === old) tokenStore.replace(token);
    } catch { /* try again next time */ }
  }, []);

  // A bad signal or a server restart must never wipe the saved login: only a real 401 signs out.
  const loadMe = useCallback(async () => {
    for (let attempt = 0; tokenStore.get(); attempt++) {
      const sent = tokenStore.get();
      try {
        setUser(await api.me());
        setOffline(false);
        setLoading(false);
        renew();
        return;
      } catch (e) {
        if (e?.response?.status === 401) {
          if (tokenStore.get() === sent) break;
          attempt = -1; // signed in again meanwhile (e.g. another tab): check the new login
          continue;
        }
        if (attempt >= 2) { setOffline(true); setLoading(false); return; }
        await wait(1500 * (attempt + 1));
      }
    }
    tokenStore.clear();
    setUser(null);
    setLoading(false);
  }, [renew]);

  const retry = useCallback(() => { setOffline(false); setLoading(true); loadMe(); }, [loadMe]);

  useEffect(() => {
    // CRITICAL: if returning from the Google OAuth callback, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (!offline) return undefined;
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [offline, retry]);

  useEffect(() => {
    if (!user) return undefined;
    const onShow = () => { if (document.visibilityState === "visible" && Date.now() - renewedAt.current > RENEW_EVERY_MS) renew(); };
    document.addEventListener("visibilitychange", onShow);
    return () => document.removeEventListener("visibilitychange", onShow);
  }, [user, renew]);

  const login = async (email, password, remember = true) => {
    const data = await api.login({ email, password });
    ownerReturn.clear();
    tokenStore.set(data.access_token, remember);
    setUser(data.user);
    return data.user;
  };

  // Exchange an Emergent Google session_id (via the proxied upstream /auth/session)
  // for a session token, stored like any other bearer token.
  const completeGoogleSession = async (session_id, signup = null) => {
    const data = signup ? await api.registerCompanyGoogle({ ...signup, session_id }) : await api.session(session_id);
    tokenStore.set(data.session_token, true);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload, call = api.register) => {
    const data = await call(payload);
    tokenStore.set(data.access_token, true);
    setUser(data.user);
    return data.user;
  };
  const registerCompany = (payload) => register(payload, api.registerCompany);

  // Platform owner: work inside a company's dashboard, then come back to the admin page.
  const enterCompany = async (cid) => {
    const data = await api.ownerEnter(cid);
    ownerReturn.set({ token: tokenStore.get(), remember: tokenStore.remembered() });
    tokenStore.set(data.access_token, false);
    depotStore.set("");
    setUser(data.user);
  };
  const backToAdmin = async () => {
    const saved = ownerReturn.get();
    ownerReturn.clear();
    depotStore.set("");
    if (!saved?.token) { tokenStore.clear(); setUser(null); return; }
    tokenStore.set(saved.token, saved.remember);
    setLoading(true);
    await loadMe();
  };

  const logout = async () => {
    if (ownerReturn.get()) { await backToAdmin(); return; }
    try { await api.logout(); } catch { /* ignore */ }
    tokenStore.clear();
    setOffline(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, offline, retry, login, register, registerCompany, logout, completeGoogleSession, enterCompany, backToAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
